var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import { useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { translate } from '@utils/i18n';
import { requestUploadUrl, uploadToS3, confirmUpload, getScanStatus } from '../services/uploadApi';
const I18N_NAMESPACE = 'features.ai_chat_widget.uploads';
/**
 * Extract a user-facing error message from an upload failure.
 *
 * The orchestrator returns useful detail via FastAPI's HTTPException
 * (e.g. "File content does not match expected type 'application/pdf'…"),
 * but AxiosError.message swallows that and produces the generic
 * "Request failed with status code 422". We unwrap `response.data.detail`
 * when it exists and fall back to localized labels otherwise. The detail
 * string itself comes from the server and is intentionally NOT translated
 * here — the orchestrator owns its own copy of those messages.
 */
function extractUploadErrorMessage(err) {
    var _a, _b, _c;
    if (axios.isAxiosError(err)) {
        const data = (_a = err.response) === null || _a === void 0 ? void 0 : _a.data;
        if (data === null || data === void 0 ? void 0 : data.detail)
            return data.detail;
        if (((_b = err.response) === null || _b === void 0 ? void 0 : _b.status) === 413)
            return translate('file_too_large', { namespace: I18N_NAMESPACE });
        if (((_c = err.response) === null || _c === void 0 ? void 0 : _c.status) === 415)
            return translate('unsupported_type', { namespace: I18N_NAMESPACE });
    }
    if (err instanceof Error)
        return err.message;
    return translate('failed_default', { namespace: I18N_NAMESPACE });
}
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_FILES_PER_MESSAGE = 5;
const SCAN_POLL_INTERVAL_MS = 2000;
const SCAN_POLL_MAX_ATTEMPTS = 30;
const ALLOWED_MIME_TYPES = new Set([
    'application/pdf',
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'image/jpeg',
    'image/png',
    'image/heic',
    'text/plain',
    'text/markdown',
    'text/x-markdown'
]);
// Extensions that browsers commonly report with empty / generic MIME types.
// We accept these by extension when `file.type` is missing or octet-stream.
const ALLOWED_EXTENSIONS = new Set(['md', 'markdown']);
// Resolve the effective Content-Type for a file. Used for BOTH the presign
// request and the S3 PUT — they must match or S3 returns 403 on signature
// mismatch (the signed URL bakes in the Content-Type).
function resolveContentType(file) {
    var _a, _b;
    if (file.type && file.type !== 'application/octet-stream') {
        return file.type;
    }
    const ext = (_b = (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '';
    if (ext === 'md' || ext === 'markdown')
        return 'text/markdown';
    // Fall back to whatever the browser gave us (which may be empty); the
    // server-side allowlist will reject anything not in ALLOWED_CONTENT_TYPES.
    return file.type;
}
// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------
function validateFiles(newFiles, existingCount) {
    const errors = [];
    const valid = [];
    const remainingSlots = MAX_FILES_PER_MESSAGE - existingCount;
    newFiles.forEach((file, index) => {
        var _a, _b;
        if (index >= remainingSlots) {
            errors.push({
                file,
                reason: 'count',
                message: translate('max_files_per_message', {
                    namespace: I18N_NAMESPACE,
                    max: MAX_FILES_PER_MESSAGE,
                    name: file.name
                })
            });
            return;
        }
        if (file.size > MAX_FILE_SIZE) {
            errors.push({
                file,
                reason: 'size',
                message: translate('file_too_large_named', {
                    namespace: I18N_NAMESPACE,
                    name: file.name,
                    limit_mb: MAX_FILE_SIZE / (1024 * 1024)
                })
            });
            return;
        }
        // Browsers report inconsistent MIME types for .md files (often
        // 'text/markdown', sometimes empty or 'application/octet-stream'). Fall
        // back to extension sniffing when the type is missing or non-specific.
        const ext = (_b = (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase()) !== null && _b !== void 0 ? _b : '';
        const allowedByExt = (file.type === '' || file.type === 'application/octet-stream') && ALLOWED_EXTENSIONS.has(ext);
        if (!ALLOWED_MIME_TYPES.has(file.type) && !allowedByExt) {
            errors.push({
                file,
                reason: 'type',
                message: translate('unsupported_type_named', {
                    namespace: I18N_NAMESPACE,
                    name: file.name,
                    type: file.type || 'unknown'
                })
            });
            return;
        }
        valid.push(file);
    });
    return { valid, errors };
}
// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useFileUpload() {
    const [files, setFiles] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const pollTimersRef = useRef(new Map());
    const isUploading = files.some((f) => f.status === 'uploading' || f.status === 'pending_scan');
    // Allow submission as soon as nothing is still in-flight. Errored files no
    // longer block the rest from being sent — getFileIds() still filters to
    // clean-only when handing IDs to the API, so the orchestrator only ingests
    // valid uploads. Failed files remain visible (with their error tooltip) in
    // the optimistic message bubble via getAllSettledFileRefs().
    const canSubmit = !isUploading;
    const updateFile = useCallback((fileId, updates) => {
        setFiles((prev) => prev.map((f) => (f.file_id === fileId ? Object.assign(Object.assign({}, f), updates) : f)));
    }, []);
    const pollScanStatus = useCallback((fileId, attempt = 0) => {
        if (attempt >= SCAN_POLL_MAX_ATTEMPTS) {
            updateFile(fileId, {
                status: 'error',
                error: translate('scan_timed_out', { namespace: I18N_NAMESPACE })
            });
            return;
        }
        const timer = setTimeout(() => {
            void (() => __awaiter(this, void 0, void 0, function* () {
                try {
                    const result = yield getScanStatus(fileId);
                    if (result.scan_status === 'pending_scan') {
                        pollScanStatus(fileId, attempt + 1);
                    }
                    else {
                        updateFile(fileId, { status: result.scan_status, progress: 100 });
                    }
                }
                catch (_a) {
                    updateFile(fileId, {
                        status: 'error',
                        error: translate('scan_status_check_failed', { namespace: I18N_NAMESPACE })
                    });
                }
            }))();
        }, SCAN_POLL_INTERVAL_MS);
        pollTimersRef.current.set(fileId, timer);
    }, [updateFile]);
    const processFile = useCallback((file) => __awaiter(this, void 0, void 0, function* () {
        const tempFileId = `temp-${Date.now()}-${file.name}`;
        let fileId = tempFileId;
        // Add placeholder entry
        setFiles((prev) => [
            ...prev,
            {
                file_id: fileId,
                name: file.name,
                mime_type: file.type || 'application/octet-stream',
                file_size_bytes: file.size,
                status: 'uploading',
                progress: 0
            }
        ]);
        // Resolve once and pass to both presign + S3 PUT so the Content-Type
        // baked into the signed URL matches the actual upload header.
        const effectiveContentType = resolveContentType(file);
        try {
            // Step 1: Get presigned URL
            const { presigned_url, file_id } = yield requestUploadUrl(file.name, effectiveContentType, file.size);
            fileId = file_id;
            // Update the file entry with real file_id
            setFiles((prev) => prev.map((f) => (f.file_id === tempFileId ? Object.assign(Object.assign({}, f), { file_id }) : f)));
            // Step 2: Upload to S3 (use the same content-type that was signed)
            yield uploadToS3(presigned_url, file, (pct) => {
                updateFile(file_id, { progress: pct });
            }, effectiveContentType);
            // Step 3: Confirm upload
            updateFile(file_id, { status: 'pending_scan', progress: 100 });
            const confirmResult = yield confirmUpload(file_id);
            if (confirmResult.scan_status === 'pending_scan') {
                // Step 4: Poll for scan completion
                pollScanStatus(file_id);
            }
            else {
                updateFile(file_id, {
                    status: confirmResult.scan_status,
                    progress: 100
                });
            }
        }
        catch (err) {
            const errorMessage = extractUploadErrorMessage(err);
            setFiles((prev) => prev.map((f) => f.file_id === fileId || f.file_id === tempFileId
                ? Object.assign(Object.assign({}, f), { status: 'error', error: errorMessage }) : f));
        }
    }), [updateFile, pollScanStatus]);
    const uploadFiles = useCallback((newFiles) => {
        const { valid, errors } = validateFiles(newFiles, files.length);
        setValidationErrors(errors);
        valid.forEach((file) => {
            void processFile(file);
        });
    }, [files.length, processFile]);
    const removeFile = useCallback((fileId) => {
        setFiles((prev) => prev.filter((f) => f.file_id !== fileId));
        // Clear any pending poll timer
        const timer = pollTimersRef.current.get(fileId);
        if (timer) {
            clearTimeout(timer);
            pollTimersRef.current.delete(fileId);
        }
    }, []);
    const clearFiles = useCallback(() => {
        // Clear all poll timers
        pollTimersRef.current.forEach((timer) => clearTimeout(timer));
        pollTimersRef.current.clear();
        setFiles([]);
        setValidationErrors([]);
    }, []);
    const getFileIds = useCallback(() => {
        return files.filter((f) => f.status === 'clean').map((f) => f.file_id);
    }, [files]);
    const getFileRefs = useCallback(() => {
        return files
            .filter((f) => f.status === 'clean')
            .map((f) => ({
            file_id: f.file_id,
            file_name: f.name,
            mime_type: f.mime_type,
            file_size_bytes: f.file_size_bytes,
            scan_status: 'clean'
        }));
    }, [files]);
    const getAllSettledFileRefs = useCallback(() => {
        // Map every settled file (clean OR failed scan) into a UserFileRef so the
        // optimistic user message bubble can render successes alongside failures.
        // Local 'error' and server-side 'infected'/'scan_error' statuses all map
        // to the 'scan_error' wire enum so the Attachment chip's status-icon path
        // renders without needing additional enum values. The error tooltip lives
        // on `error_message` and surfaces via the chip's title attribute.
        return files
            .filter((f) => f.status === 'clean' || f.status === 'error' || f.status === 'scan_error' || f.status === 'infected')
            .map((f) => ({
            file_id: f.file_id,
            file_name: f.name,
            mime_type: f.mime_type,
            file_size_bytes: f.file_size_bytes,
            scan_status: f.status === 'clean' ? 'clean' : 'scan_error',
            error_message: f.status === 'clean' ? undefined : f.error
        }));
    }, [files]);
    return {
        files,
        validationErrors,
        isUploading,
        canSubmit,
        uploadFiles,
        removeFile,
        clearFiles,
        getFileIds,
        getFileRefs,
        getAllSettledFileRefs
    };
}
